import {
    TokenListMetadata,
    ExternalYieldInfo,
    StrategyDuration,
    MarketInformationAssetDataExpanded,
    LendingStrategy,
    RawLendingStrategy
} from "@bridgesplit/abf-sdk";
import { SortDirection } from "@bridgesplit/utils";
import Decimal from "decimal.js";

export interface LendingStrategyFilter {
    userAddress?: string;
    principalMints?: string[];
    addresses?: string[];
    showArchived?: boolean;
}

export interface LendingStrategyPaginationParams {
    sortType: StrategySortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}

export type LendingStrategyPagination = LendingStrategyPaginationParams & LendingStrategyFilter;

export enum StrategySortType {
    NetAssetValue,
    RecentlyCreated
}

export interface StrategyExpanded extends Omit<StrategyInfo, "strategy"> {
    totalBalance: number; // is amount available to withdraw
    principalMetadata: TokenListMetadata;
    collateral: StrategyCollateralExpanded[];
    principalUsdPrice: number;

    //Expanded fields
    strategy: LendingStrategyExpanded;
}

export interface StrategyCollateralExpanded {
    metadata: TokenListMetadata;
    durationAndApys: Record<string, number>;
    ltvInfo: LtvInfo;
}

export interface LendingStrategyExpanded extends LendingStrategy {
    fixedApy: number;
    wAvgApy: number;
}

export interface RawCollateralAllocationInfo {
    maxAllocationPct: string;
    currentAllocationAmount: string;
}

export interface RawAssetTerms {
    durationAndApys: [StrategyDuration, number][];
    allocationInfo: RawCollateralAllocationInfo;
}

//API return type

export interface RawCapMonitor {
    startTime1hr: number;
    startTime24hr: number;
    principal1hr: string;
    principal24hr: string;
}

export interface RawStrategyInfo {
    strategy: RawLendingStrategy; // "LendingStrategy" correlates with 'Strategy' on the BE, but unable to use similar name due to strategy.ts export
    terms: {
        assetTerms: Record<string, RawAssetTerms>;
        supplyCaps: {
            max1hr: string;
            max24hr: string;
            maxOutstanding: string;
        };
        borrowCaps: {
            max1hr: string;
            max24hr: string;
            maxOutstanding: string;
        };
        withdrawCaps: {
            max1hr: string;
            max24hr: string;
            maxOutstanding: string;
        };
    };
    externalYieldInfo?: ExternalYieldInfo;
    borrowCapMonitor: RawCapMonitor;
    supplyCapMonitor: RawCapMonitor;
    withdrawCapMonitor: RawCapMonitor;
}

export interface StrategyInfoResponse {
    strategies: StrategyInfo[];
    total: number;
}
export interface StrategyInfo {
    strategy: LendingStrategy; // "LendingStrategy" correlates with 'Strategy' on the BE, but unable to use similar name due to strategy.ts export
    terms: StrategyTerms;
    externalYieldInfo?: ExternalYieldInfo;

    //Cap Monitors
    borrowCapMonitor: CapMonitor;
    supplyCapMonitor: CapMonitor;
    withdrawCapMonitor: CapMonitor;
}

export interface AssetTerms {
    durationAndApys: [StrategyDuration, number][];
    allocationInfo: CollateralAllocationInfo;
}

export interface StrategyTerms {
    assetTerms: Record<string, AssetTerms>; //Key is mint, value is a tuple of StrategyDuration and apy
    supplyCaps: PrincipalCaps;
    borrowCaps: PrincipalCaps;
    withdrawCaps: PrincipalCaps;
}
export interface CapMonitor {
    startTime1hr: Decimal;
    startTime24hr: Decimal;
    principal1hr: Decimal | null; // Using Decimal to match API response, convert from string when needed
    principal24hr: Decimal | null; // Using Decimal to match API response, convert from string when needed
}

export type PrincipalCaps = {
    max1hr: Decimal | null;
    max24hr: Decimal | null;
    maxOutstanding: Decimal | null;
};

export interface CollateralAllocationInfo {
    maxAllocationPct: Decimal | null;
    currentAllocationAmount: Decimal | null;
}

export interface LtvInfo {
    ltv: number;
    liquidationThreshold: number;
}

export type CollateralWithTerms = {
    metadata: TokenListMetadata;
    //  all LTV infos if viewing across mutliple strateg
} & (
    | { allLtvInfos: MarketInformationAssetDataExpanded[] }
    // single LTV info if strategy already exists
    | { ltvInfo: LtvInfo }
);

export enum OriginationCapStatus {
    Accepting = "Accepting",
    Closed = "Closed"
}

export type MintToDurationAndApys = { [mint: string]: [StrategyDuration, number][] };

export const STRATEGY_TEMPLATE_ALL_IDENTIFIER = "all";
export const STRATEGY_TEMPLATE_CUSTOMIZE_IDENTIFIER = "custom";
